/**
 * ITER284 Phase D — Client-side Web Vitals + custom metrics capture.
 *
 * Zéro impact perçu : les métriques sont bufférisées puis flushées via
 * `navigator.sendBeacon` (ne bloque JAMAIS la navigation). Fire-and-
 * forget vers POST /api/metrics/client — l'API répond 200 même en cas
 * d'erreur DB.
 *
 * Échantillonnage 100% en dev, 10% en production (tunable via
 * `REACT_APP_METRICS_SAMPLE_RATE`). Le collect ne démarre QUE pour les
 * utilisateurs légitimes — on ne pénalise pas les inscriptions.
 *
 * Aucune donnée sensible collectée :
 *   - Route (pathname anonymisé)
 *   - Métriques web-vitals (LCP, INP, CLS, FCP, TTFB)
 *   - Identifiant de navigation (UUID généré au boot, non lié au user)
 *
 * Zéro-doublon : ce module ne recrée pas `web-vitals`, il consomme le
 * package officiel via l'API `onLCP/onINP/onCLS/onFCP/onTTFB`.
 */
import { onCLS, onFCP, onINP, onLCP, onTTFB } from 'web-vitals';

const API = process.env.REACT_APP_BACKEND_URL || '';
const ENDPOINT = `${API}/api/metrics/client`;
const SAMPLE_RATE = Number(process.env.REACT_APP_METRICS_SAMPLE_RATE
  || (process.env.NODE_ENV === 'production' ? 0.10 : 1.0));

const BUFFER = [];
let flushTimer = null;
const NAV_ID = (typeof crypto !== 'undefined' && crypto.randomUUID)
  ? crypto.randomUUID() : Math.random().toString(36).slice(2);

function anonRoute() {
  try {
    let p = window.location.pathname || '/';
    // Anonymize IDs in path: /users/abc123 → /users/:id
    p = p.replace(/\/[a-f0-9-]{8,}/gi, '/:id')
         .replace(/\/\d{4,}/g, '/:n');
    return p.slice(0, 128);
  } catch { return '/'; }
}

function shouldSample() {
  return Math.random() < SAMPLE_RATE;
}

function push(metric, value, rating) {
  if (!shouldSample() && metric !== 'js_error') return;
  BUFFER.push({
    metric,
    value: Number(value) || 0,
    rating: rating || null,
    route: anonRoute(),
    navigation_id: NAV_ID,
  });
  scheduleFlush();
}

function scheduleFlush() {
  if (flushTimer) return;
  flushTimer = setTimeout(flush, 4000);
}

function flush() {
  flushTimer = null;
  if (BUFFER.length === 0) return;
  const batch = { samples: BUFFER.splice(0, BUFFER.length) };
  const body = JSON.stringify(batch);
  try {
    if (navigator.sendBeacon) {
      const blob = new Blob([body], { type: 'application/json' });
      navigator.sendBeacon(ENDPOINT, blob);
      return;
    }
  } catch { /* fall through */ }
  // Fallback fetch — keepalive so it survives page navigation.
  try {
    fetch(ENDPOINT, {
      method: 'POST', body,
      headers: { 'Content-Type': 'application/json' },
      keepalive: true, credentials: 'include',
    }).catch(() => { /* silent */ });
  } catch { /* silent */ }
}

// Register web-vitals listeners once.
try {
  onLCP((m) => push('lcp', m.value, m.rating));
  onINP((m) => push('inp', m.value, m.rating));
  onCLS((m) => push('cls', m.value, m.rating));
  onFCP((m) => push('fcp', m.value, m.rating));
  onTTFB((m) => push('ttfb', m.value, m.rating));
} catch { /* ignore in very old browsers */ }

// iter284 Phase D — API timing capture (via axios interceptor).
// Records ONLY the API path (never query/body) and duration for the
// current navigation route. Sampled at the module rate.
try {
  const axios = require('axios');
  axios.interceptors.request.use((cfg) => {
    // eslint-disable-next-line no-param-reassign
    cfg.metadata = { start: performance.now() };
    return cfg;
  });
  const record = (response, error) => {
    try {
      const cfg = response?.config || error?.config;
      if (!cfg?.metadata?.start) return;
      const dur = performance.now() - cfg.metadata.start;
      const path = (cfg.url || '').replace(API, '').split('?')[0];
      if (!path.startsWith('/api/')) return;
      // Store in the `api` metric channel using route=path so slow-routes
      // aggregation in Admin dashboard groups by real endpoint.
      BUFFER.push({
        metric: 'api',
        value: Number(dur) || 0,
        rating: dur < 300 ? 'good' : dur < 1000 ? 'needs-improvement' : 'poor',
        route: path.slice(0, 128),
        navigation_id: NAV_ID,
      });
      scheduleFlush();
    } catch { /* silent */ }
  };
  axios.interceptors.response.use(
    (r) => { record(r); return r; },
    (e) => { record(null, e); return Promise.reject(e); },
  );
} catch { /* silent — axios not available (SSR / edge cases) */ }

// JS errors — sampled at 100% for critical visibility.
if (typeof window !== 'undefined') {
  window.addEventListener('error', (e) => {
    try {
      BUFFER.push({
        metric: 'js_error', value: 1, rating: null,
        route: anonRoute(), navigation_id: NAV_ID,
      });
      scheduleFlush();
    } catch { /* silent */ }
  });
  window.addEventListener('beforeunload', flush);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flush();
  });
}

export const NAVIGATION_ID = NAV_ID;
